import React from 'react';
import { Calendar, X } from 'lucide-react';
import { Calendar as DayPicker, createStaticRanges } from 'react-date-range';
import {
  startOfDay, endOfDay, subDays,
  startOfWeek, endOfWeek, subWeeks,
  startOfMonth, endOfMonth, subMonths,
} from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Shipment/invoice filters shared by the Accountant and Admin dashboards, so
// both screens offer the same set and behave identically.

// Weeks start Monday, as the business counts them.
const WEEK = { weekStartsOn: 1 };

// Quick ranges for the shipment date-range picker.
export const DATE_PRESETS = createStaticRanges([
  { label: 'Today', range: () => ({ startDate: startOfDay(new Date()), endDate: endOfDay(new Date()) }) },
  { label: 'Yesterday', range: () => ({ startDate: startOfDay(subDays(new Date(), 1)), endDate: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'This Week', range: () => ({ startDate: startOfWeek(new Date(), WEEK), endDate: endOfDay(new Date()) }) },
  { label: 'Last Week', range: () => ({ startDate: startOfWeek(subWeeks(new Date(), 1), WEEK), endDate: endOfWeek(subWeeks(new Date(), 1), WEEK) }) },
  { label: 'This Month', range: () => ({ startDate: startOfMonth(new Date()), endDate: endOfDay(new Date()) }) },
  { label: 'Last Month', range: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'Last 30 Days', range: () => ({ startDate: startOfDay(subDays(new Date(), 29)), endDate: endOfDay(new Date()) }) },
]);

// "Unpaid since" presets — each resolves to the earliest date of the period.
export const UNPAID_SINCE_PRESETS = [
  { label: 'Today', date: () => startOfDay(new Date()) },
  { label: 'Yesterday', date: () => startOfDay(subDays(new Date(), 1)) },
  { label: 'This Week', date: () => startOfWeek(new Date(), WEEK) },
  { label: 'Last Week', date: () => startOfWeek(subWeeks(new Date(), 1), WEEK) },
  { label: 'This Month', date: () => startOfMonth(new Date()) },
  { label: 'Last Month', date: () => startOfMonth(subMonths(new Date(), 1)) },
  { label: 'Last 30 Days', date: () => startOfDay(subDays(new Date(), 29)) },
];

// Dates arrive as a single value, an array (multi-leg shipments) or a
// JSON-encoded array — reduce to the timestamps we can compare.
const toTimestamps = (value) => {
  const values = Array.isArray(value)
    ? value
    : (typeof value === 'string' && value.trim().startsWith('[')
      ? (() => { try { return JSON.parse(value); } catch { return [value]; } })()
      : [value]);
  return values
    .map((v) => {
      if (!v) return 0;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? 0 : t;
    })
    .filter((t) => t > 0);
};

// Payment status of a shipment's invoice (first record for the AWB/BL), as
// stored in invoices_details.status. No invoice → null.
//
// The invoice date is not a column: it lives inside the invoice_data array, as
// its own column on air records and nested under `invoice` on sea ones. Reading
// only the top level fell through to the record's created_at, which made every
// invoice look as if it were raised today — "Overdue by" then matched nothing
// above 0, and "Unpaid since" filtered on the wrong day.
export const getInvoicePayment = (invoiceMap, shipmentNo) => {
  const rec = invoiceMap?.[shipmentNo];
  const raw = rec && (Array.isArray(rec) ? rec[0] : rec);
  if (!raw) return null;
  const inv = raw.invoice || raw;
  const entries = [raw.invoiceData, raw.invoice_data, inv.invoiceData, inv.invoice_data]
    .find((v) => Array.isArray(v) && v.length) || [];
  const firstEntry = entries[0] || null;
  return {
    status: String(raw.status ?? inv.status ?? 'unpaid').toLowerCase(),
    invoiceDate: raw.invoiceDate ?? raw.invoice_date ?? inv.invoiceDate ?? inv.invoice_date
      ?? firstEntry?.invoiceDate ?? firstEntry?.invoice_date
      ?? raw.createdAt ?? raw.created_at ?? null,
  };
};

// Whole days elapsed between the invoice date and today (local calendar days).
const daysSinceInvoice = (invoiceDate) => {
  const ts = toTimestamps(invoiceDate)[0];
  if (!ts) return null;
  const invDay = new Date(ts); invDay.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.floor((today - invDay) / 86400000);
};

/**
 * Build the predicate the shipment lists filter on. Returns
 * (shipmentNo, hasInvoicePdf) => boolean.
 */
export const makePaymentMatcher = ({ invoiceMap, paymentFilter = 'all', unpaidSince = null, overdueDays = '' }) => {
  const overdueN = overdueDays === '' ? null : Number(overdueDays);
  const overdueActive = overdueN !== null && Number.isFinite(overdueN) && overdueN >= 0;

  return (shipmentNo, hasInvoicePdf) => {
    if (paymentFilter === 'all' && !unpaidSince && !overdueActive) return true;
    const pay = getInvoicePayment(invoiceMap, shipmentNo);
    if (paymentFilter === 'no_invoice') return !pay && !hasInvoicePdf;
    if (!pay) return false; // remaining filters are about an existing invoice
    if (paymentFilter !== 'all' && pay.status !== paymentFilter) return false;
    if (unpaidSince) {
      if (pay.status === 'paid') return false; // "unpaid since" only makes sense for open invoices
      const ts = toTimestamps(pay.invoiceDate)[0];
      if (!ts || ts < new Date(unpaidSince).setHours(0, 0, 0, 0)) return false;
    }
    if (overdueActive) {
      // "Still unpaid N days after the invoice date" — a paid invoice is never overdue.
      if (pay.status === 'paid') return false;
      const elapsed = daysSinceInvoice(pay.invoiceDate);
      if (elapsed === null || elapsed < overdueN) return false;
    }
    return true;
  };
};

/** All / Invoice Pending / Unpaid / Partially Paid / Paid. */
export const PaymentStatusFilter = ({ value, onChange, className = 'w-full' }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    title="Filter by invoice / payment status"
    style={value !== 'all' ? { color: '#fff' } : undefined}
    className={`ql-select ${value !== 'all' ? 'ql-select--invert' : ''} h-11 rounded-xl px-3.5 text-[13px] font-semibold border outline-none transition focus:ring-4 cursor-pointer ${className} ${
      value !== 'all'
        ? 'bg-emerald-600 border-emerald-600 text-white focus:ring-emerald-500/20'
        : 'bg-slate-50 border-slate-200 text-slate-700 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-200'
    }`}
  >
    <option value="all">All Invoices</option>
    <option value="no_invoice">Invoice Pending</option>
    <option value="unpaid">Unpaid</option>
    <option value="partially_paid">Partially Paid</option>
    <option value="paid">Paid</option>
  </select>
);

/** Open invoices dated on or after a day — presets down the side, calendar beside them. */
export const UnpaidSinceFilter = ({ value, onChange, open, onOpenChange, className = 'w-full' }) => (
  <div className={`relative ${className}`}>
    <div className="relative">
      <input
        type="text"
        value={value ? `Unpaid since ${value.toLocaleDateString('en-GB')}` : ''}
        onClick={() => onOpenChange(true)}
        readOnly
        placeholder="Unpaid since"
        title="Show invoices still unpaid / partially paid, dated on or after this date"
        className={`h-11 rounded-xl px-3.5 text-[13px] font-semibold border outline-none transition focus:ring-4 cursor-pointer w-full pr-10 placeholder-slate-400 ${
          value
            ? 'bg-rose-50 border-rose-300 text-rose-700 focus:ring-rose-500/15 dark:bg-rose-500/10 dark:border-rose-500/40 dark:text-rose-300'
            : 'bg-slate-50 border-slate-200 text-slate-700 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-200'
        }`}
      />
      {value ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(null); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-500/20"
          title="Clear"
        >
          <X size={14} />
        </button>
      ) : (
        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
      )}
    </div>
    {open && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" onClick={() => onOpenChange(false)}>
        <div className="ql-daterange rounded-2xl shadow-2xl overflow-hidden w-full max-w-fit flex flex-col
                        bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
          <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
            <p className="text-[14px] font-bold text-slate-900 dark:text-white">Show invoices unpaid since</p>
            <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">Open (unpaid / partially paid) invoices dated on or after this day</p>
          </div>
          <div className="flex">
            <div className="w-40 border-r border-slate-100 dark:border-slate-800 py-2">
              {UNPAID_SINCE_PRESETS.map((p) => {
                const d = p.date();
                const active = value && startOfDay(value).getTime() === d.getTime();
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => { onChange(d); onOpenChange(false); }}
                    className={`w-full text-left px-4 py-2 text-[13px] font-semibold transition ${
                      active
                        ? 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/15'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <DayPicker
              date={value || new Date()}
              onChange={(d) => { onChange(d); onOpenChange(false); }}
              minDate={new Date(2020, 0, 1)}
              maxDate={new Date()}
              color="#2563eb"
            />
          </div>
          <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={() => { onChange(null); onOpenChange(false); }}
              className="h-10 px-4 rounded-xl text-[13px] font-bold transition
                         border border-slate-200 bg-white text-slate-600 hover:bg-slate-50
                         dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

/** Invoices still open N days after their invoice date. */
export const OverdueDaysFilter = ({ value, onChange, className = 'w-full' }) => (
  <div className={`relative flex items-center ${className}`} title="Invoices still unpaid or partially paid this many days after their invoice date">
    <span className="absolute left-3.5 text-[11.5px] font-bold text-slate-500 dark:text-slate-400 pointer-events-none">Overdue by</span>
    <input
      type="text"
      inputMode="numeric"
      maxLength={3}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder="0"
      className={`h-11 rounded-xl px-3.5 text-[13px] font-semibold border outline-none transition focus:ring-4 cursor-pointer w-full pl-[88px] pr-12 placeholder-slate-400 ${
        value !== ''
          ? 'bg-amber-50 border-amber-300 text-amber-700 focus:ring-amber-500/15 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-300'
          : 'bg-slate-50 border-slate-200 text-slate-700 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-200'
      }`}
    />
    <span className={`absolute right-3.5 text-[11.5px] font-bold pointer-events-none ${
      value !== '' ? 'text-amber-500' : 'text-slate-400 dark:text-slate-500'}`}>days</span>
  </div>
);
